import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { trpcClient } from "@/lib/trpc";
import { useRoleAccess } from "@/hooks/useRoleAccess";
import { ComboboxFK } from "@/components/shared/ComboboxFK";
import { ComboboxFKMulti } from "@/components/shared/ComboboxFKMulti";
import { useUserLookup, useCustomerLookup } from "@/hooks/useFKLookup";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { OverviewTab } from "./components/OverviewTab";
import { ProductionTab } from "./components/ProductionTab";
import { PaperSupplyTab } from "./components/PaperSupplyTab";
import { DeliveryTab } from "./components/DeliveryTab";
import { FinanceTab } from "./components/FinanceTab";
import { DashboardDrillDownDrawer, type DrillDownConfig } from "./components/DashboardDrillDownDrawer";
import { OrderStatus, DeliveryStatus, getStatusConfig, getStatusLabel } from "@/config/status-registry";

import { toVnDateString, vnStartOfMonth } from "@apv-erp/status";
// ─── Period options ───
const PERIOD_OPTIONS = [
  { key: "today", label: "Hôm nay" },
  { key: "week", label: "Tuần này" },
  { key: "month", label: "Tháng này" },
  { key: "quarter", label: "Quý này" },
  { key: "year", label: "Năm nay" },
] as const;

const DELIVERY_STATUS_HEX: Record<string, string> = {
  DRAFT: "#9ca3af",
  SCHEDULED: "#06b6d4",
  IN_TRANSIT: "#f59e0b",
  DELIVERED: "#10b981",
  CANCELLED: "#ef4444",
};
const DELIVERY_STATUS_INFO: Record<string, { label: string; hex: string }> = Object.fromEntries(
  Object.entries(DELIVERY_STATUS_HEX).map(([k, hex]) => [
    k,
    { label: getStatusLabel("DeliveryStatus", k), hex },
  ])
);

export default function DashboardPage() {
  const initialTab =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("tab") || "overview"
      : "overview";
  const [activeTab, setActiveTab] = useState<string>(initialTab);

  const handleTabChange = (newTab: string) => {
    setActiveTab(newTab);
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      if (newTab === "overview") {
        url.searchParams.delete("tab");
      } else {
        url.searchParams.set("tab", newTab);
      }
      window.history.replaceState(null, "", url.toString());
    }
  };

  const [period, setPeriod] = useState<string>("month");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const { canViewAllData, userId } = useRoleAccess();
  const userLookup = useUserLookup();
  const customerLookup = useCustomerLookup();
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedCustomerIds, setSelectedCustomerIds] = useState<string[]>([]);
  const [selectedOrderStatuses, setSelectedOrderStatuses] = useState<string[]>([]);
  // Phạm vi dữ liệu đơn hàng không phải "Tất cả" → chỉ số liệu của chính mình (theo bảng phân quyền, không theo tên vai trò)
  const effectiveUserId = !canViewAllData ? userId : selectedUserId || undefined;

  const [drillDownConfig, setDrillDownConfig] = useState<DrillDownConfig>({
    open: false,
    type: "",
    title: "",
  });

  const queryPeriod =
    period === "custom" && dateFrom && dateTo
      ? `custom:${dateFrom}:${dateTo}`
      : period;

  const { data: analytics, isLoading } = useQuery({
    queryKey: [
      "dashboard-analytics",
      queryPeriod,
      effectiveUserId || "all",
      selectedCustomerIds,
      selectedOrderStatuses,
    ],
    queryFn: () => {
      const params: any = { period: queryPeriod };
      if (effectiveUserId) params.salesUserId = effectiveUserId;
      if (selectedCustomerIds.length > 0)
        params.customerIds = selectedCustomerIds;
      if (selectedOrderStatuses.length > 0)
        params.orderStatuses = selectedOrderStatuses;
      return trpcClient.dashboard.analytics.query(params);
    },
    staleTime: 30_000,
    retry: false,
  });

  // Extract Data from Ground Truth Backend
  const kpis = (analytics as any)?.kpis;
  const quickStats = (analytics as any)?.quickStats;
  const activities = (analytics as any)?.recentActivities ?? [];
  const monthlyTrend = (analytics as any)?.monthlyTrend ?? [];
  const deliveryByWeek = (analytics as any)?.deliveryByWeek ?? [];
  const deliveryByStatus = (analytics as any)?.deliveryByStatus ?? [];
  const earlyWarnings = (analytics as any)?.earlyWarnings;

  // Phase 40 Paper stats
  const paperStats = (analytics as any)?.paperStats ?? {
    totalMaterials: 0,
    totalSlitted: 0,
    pendingSlit: 0,
    longGrainCount: 0,
    shortGrainCount: 0,
    totalPallets: 0,
    totalStockPcs: 0,
    topSurplusItems: [],
  };

  // Finance & Delivery
  const financeStats = (analytics as any)?.financeStats ?? {
    totalRevenue: 0,
    cashIn: 0,
    cashOut: 0,
    totalReceivables: 0,
    cashFlowTrend: [],
  };
  const deliveryStats = (analytics as any)?.deliveryStats ?? { otifRate: 0 };

  const deliveryDonutData = deliveryByStatus.map((d: any) => ({
    label: getStatusConfig("DeliveryStatus", d.status).label || d.status,
    value: d.count || 0,
    color: DELIVERY_STATUS_INFO[d.status]?.hex || "#9ca3af",
  }));

  return (
    <div className="space-y-3 pb-4 p-2 md:p-4">
      {/* Thanh Bộ Lọc Tối Giản */}
      <div className="grid grid-cols-2 md:flex md:flex-wrap items-center gap-2">
        <div className="min-w-0 md:w-[220px]">
          <ComboboxFKMulti
            value={selectedCustomerIds}
            onChange={setSelectedCustomerIds}
            options={customerLookup.options}
            loading={customerLookup.isLoading}
            label="Khách hàng"
            hideLabel
            placeholder="Khách hàng"
          />
        </div>
        <div className="min-w-0 md:w-[200px]">
          <ComboboxFKMulti
            value={selectedOrderStatuses}
            onChange={setSelectedOrderStatuses}
            options={Object.entries(OrderStatus).map(([id, cfg]) => ({ id, label: cfg.label }))}
            label="Trạng thái đơn hàng"
            hideLabel
            placeholder="Trạng thái đơn"
          />
        </div>
        {canViewAllData && (
          <div className="min-w-0 md:w-[170px]">
            <ComboboxFK
              value={selectedUserId}
              onChange={setSelectedUserId}
              options={userLookup.options}
              loading={userLookup.isLoading}
              label="Nhân viên"
              hideLabel
              placeholder="Tất cả NV"
            />
          </div>
        )}
        <div className="min-w-0 md:w-[160px]">
          <ComboboxFK
            value={period}
            onChange={(v) => {
              if (v) setPeriod(v);
              if (v === "custom" && !dateFrom) {
                setDateFrom(vnStartOfMonth());
                setDateTo(toVnDateString());
              }
            }}
            options={[
              ...PERIOD_OPTIONS.map((opt) => ({
                id: opt.key,
                label: opt.label,
              })),
              { id: "custom", label: "Tùy chỉnh" },
            ]}
            label="Kỳ thời gian"
            hideLabel
            placeholder="Chọn kỳ"
          />
        </div>
        {period === "custom" && (
          <div className="col-span-2 md:col-span-1 flex items-center gap-1.5 bg-card border border-border rounded-lg px-2 py-1 shadow-xs">
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="h-7 text-xs border-0 bg-transparent text-foreground focus:outline-none flex-1 md:w-[120px]"
            />
            <span className="text-xs text-muted-foreground">→</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="h-7 text-xs border-0 bg-transparent text-foreground focus:outline-none flex-1 md:w-[120px]"
            />
          </div>
        )}
      </div>

      {/* 5 Tabs Chuẩn Hóa Điều Hành Nhà Máy In */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="mb-3">
          <TabsTrigger value="overview">Tổng quan</TabsTrigger>
          <TabsTrigger value="production">Sản xuất</TabsTrigger>
          <TabsTrigger value="paper">Cấp phát giấy</TabsTrigger>
          <TabsTrigger value="delivery">Giao hàng</TabsTrigger>
          <TabsTrigger value="finance">Dòng tiền</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <OverviewTab
            isLoading={isLoading}
            kpis={kpis}
            quickStats={quickStats}
            monthlyTrend={monthlyTrend}
            activities={activities}
            earlyWarnings={earlyWarnings}
            onDrillDown={(cfg) =>
              setDrillDownConfig({ ...cfg, open: true, period: queryPeriod })
            }
          />
        </TabsContent>

        <TabsContent value="production">
          <ProductionTab
            isLoading={isLoading}
            kpis={kpis}
            quickStats={quickStats}
            earlyWarnings={earlyWarnings}
            onDrillDown={(cfg) =>
              setDrillDownConfig({ ...cfg, open: true, period: queryPeriod })
            }
          />
        </TabsContent>

        <TabsContent value="paper">
          <PaperSupplyTab
            isLoading={isLoading}
            paperStats={paperStats}
            earlyWarnings={earlyWarnings}
            onDrillDown={(cfg) =>
              setDrillDownConfig({ ...cfg, open: true, period: queryPeriod })
            }
          />
        </TabsContent>

        <TabsContent value="delivery">
          <DeliveryTab
            isLoading={isLoading}
            deliveryByWeek={deliveryByWeek}
            deliveryDonutData={deliveryDonutData}
            otifRate={deliveryStats.otifRate}
            earlyWarnings={earlyWarnings}
            onDrillDown={(cfg) =>
              setDrillDownConfig({ ...cfg, open: true, period: queryPeriod })
            }
          />
        </TabsContent>

        <TabsContent value="finance">
          <FinanceTab
            isLoading={isLoading}
            financeStats={financeStats}
            earlyWarnings={earlyWarnings}
            onDrillDown={(cfg) =>
              setDrillDownConfig({ ...cfg, open: true, period: queryPeriod })
            }
          />
        </TabsContent>
      </Tabs>

      {/* Slide-Over Drawer Drill-Down */}
      <DashboardDrillDownDrawer
        config={drillDownConfig}
        onClose={() =>
          setDrillDownConfig((prev) => ({ ...prev, open: false }))
        }
      />
    </div>
  );
}
